import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  Camera,
  ChevronRight,
  Users,
  TrendingDown,
  type LucideIcon,
} from 'lucide-react';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js';
import { Bar as BarChart } from 'react-chartjs-2';
import { Button, Card, Pill } from '@nako/ui';
import { buildProfitLoss, buildTrialBalance, formatMoney, formatMoneyCompact } from '@nako/core';
import type { Account, JournalEntry, JournalLine, Receipt, Reminder } from '@nako/types';
import { isHealthcareBusiness } from '@nako/types';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { useUiStore } from '@/stores/ui';
import { supabase } from '@/lib/supabase';
import { localYmd } from '@/lib/journal-postings';
import { makeT } from '@/lib/i18n';
import { RecallWidget } from '@/components/dental/RecallWidget';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

interface Bundle {
  accounts: Account[];
  entries: JournalEntry[];
  lines: JournalLine[];
  receipts: Receipt[];
  reminders: Reminder[];
}

export default function Dashboard() {
  const business = useAuthStore((s) => s.business);
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const taxAuth = regionConfig.taxAuthority;
  const lang = useUiStore((s) => s.lang);
  const view = useUiStore((s) => s.view);
  const t = makeT(lang);
  const [bundle, setBundle] = useState<Bundle | null>(null);

  const [pendingRemindersCount, setPendingRemindersCount] = useState(0);

  useEffect(() => {
    if (!business) return;
    void (async () => {
      // First fetch entries so we can filter lines by entry_id (defence in
      // depth — RLS does this server-side, but the explicit filter prevents
      // a single misconfigured policy from leaking the whole ledger).
      const entriesResp = await supabase
        .from('journal_entries')
        .select('*')
        .eq('business_id', business.id);
      const entryIds = (entriesResp.data ?? []).map((e) => e.id);

      const [accounts, lines, receipts, reminders, pendingCount] = await Promise.all([
        supabase.from('accounts').select('*').eq('business_id', business.id),
        entryIds.length === 0
          ? Promise.resolve({ data: [] })
          : supabase.from('journal_lines').select('*').in('entry_id', entryIds),
        supabase
          .from('receipts')
          .select('*')
          .eq('business_id', business.id)
          .order('uploaded_at', { ascending: false })
          .limit(5),
        supabase
          .from('reminders')
          .select('*')
          .eq('business_id', business.id)
          .order('due_date', { ascending: true })
          .limit(5),
        supabase
          .from('reminders')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', business.id)
          .eq('status', 'pending'),
      ]);
      setBundle({
        accounts: accounts.data ?? [],
        entries: entriesResp.data ?? [],
        lines: lines.data ?? [],
        receipts: receipts.data ?? [],
        reminders: reminders.data ?? [],
      });
      setPendingRemindersCount(pendingCount.count ?? 0);
    })();
  }, [business]);

  const monthly = useMemo(() => {
    if (!bundle) return null;
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    return buildProfitLoss(
      { accounts: bundle.accounts, entries: bundle.entries, lines: bundle.lines },
      start,
      new Date(),
    );
  }, [bundle]);

  const sixMonthBars = useMemo(() => {
    if (!bundle) return null;
    const labels: string[] = [];
    const income: number[] = [];
    const expense: number[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      const start = new Date(d);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const pl = buildProfitLoss(
        { accounts: bundle.accounts, entries: bundle.entries, lines: bundle.lines },
        start,
        end,
      );
      labels.push(d.toLocaleString('en', { month: 'short' }));
      income.push(pl.totalIncome);
      expense.push(pl.totalExpense);
    }
    return { labels, income, expense };
  }, [bundle]);

  const expenseBreakdown = useMemo(() => {
    if (!bundle) return null;
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    // Local date, NOT toISOString — the UTC shift (we're UTC+2) would set the
    // window start to the last day of the PREVIOUS month.
    const startIso = localYmd(start);

    const entriesById = new Map(bundle.entries.map((e) => [e.id, e]));
    const accountsById = new Map(bundle.accounts.map((a) => [a.id, a]));

    const totalsByAccount = new Map<string, { name: string; gl_code: string; total: number }>();
    let salary = 0;

    for (const line of bundle.lines) {
      const entry = entriesById.get(line.entry_id);
      if (!entry || !entry.is_posted) continue;
      if ((entry.entry_date ?? '') < startIso) continue;

      const acc = accountsById.get(line.account_id);
      if (!acc) continue;
      const isExpense = acc.type === 'expense' || acc.gl_code?.startsWith('5');
      if (!isExpense) continue;

      const net = (Number(line.debit) || 0) - (Number(line.credit) || 0);
      if (net <= 0) continue;

      if (acc.gl_code === '5100') salary += net;

      const cur = totalsByAccount.get(acc.id) ?? {
        name: acc.name,
        gl_code: acc.gl_code,
        total: 0,
      };
      cur.total += net;
      totalsByAccount.set(acc.id, cur);
    }

    const major = Array.from(totalsByAccount.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 4);

    return { salary, major };
  }, [bundle]);

  const cashBalance = useMemo(() => {
    if (!bundle) return 0;
    const cashAccount = bundle.accounts.find((a) => a.gl_code === '1000');
    if (!cashAccount) return 0;
    let bal = 0;
    for (const line of bundle.lines) {
      if (line.account_id !== cashAccount.id) continue;
      bal += (Number(line.debit) || 0) - (Number(line.credit) || 0);
    }
    return bal;
  }, [bundle]);

  const trialBalance = useMemo(() => {
    if (!bundle || view !== 'accountant') return null;
    return buildTrialBalance({
      accounts: bundle.accounts,
      entries: bundle.entries,
      lines: bundle.lines,
    });
  }, [bundle, view]);

  const receipts = bundle?.receipts ?? [];
  const reminders = bundle?.reminders ?? [];

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      {/* Hero */}
      <div className="rounded-card-lg bg-ink text-paper shadow-card mb-4 p-6">
        <p className="text-paper-2 text-sm">{t('dash.this_month')}</p>
        <p className="font-display tnum mt-1 text-4xl font-semibold md:text-5xl">
          {monthly ? formatMoney(monthly.netProfit, regionCode) : formatMoney(0, regionCode)}
        </p>
        <p className="text-paper-2 mt-1 text-sm">
          {monthly
            ? `Income ${formatMoneyCompact(monthly.totalIncome, regionCode)} · Expenses ${formatMoneyCompact(monthly.totalExpense, regionCode)}`
            : 'No data yet — scan your first receipt.'}
        </p>
        <Link to="/app/receipts/scan">
          <Button variant="secondary" full className="bg-paper-2 text-ink mt-4 border-transparent">
            <Camera size={18} className="mr-1" /> {t('dash.scan_receipt')}
          </Button>
        </Link>
      </div>

      {/* Quick actions */}
      <div className="mb-4 grid grid-cols-3 gap-2 md:gap-3">
        <QuickAction
          to="/app/receipts/scan"
          icon={Camera}
          title="Scan"
          subtitle="Add receipt"
          tone="accent"
        />
        <QuickAction
          to="/app/reports"
          icon={BarChart3}
          title="Reports"
          subtitle="P&L · TB · BS"
          tone="blue"
        />
        <QuickAction
          to="/app/reminders"
          icon={Bell}
          title="Reminders"
          subtitle={pendingRemindersCount > 0 ? `${pendingRemindersCount} pending` : 'All clear'}
          tone="green"
          badge={pendingRemindersCount}
        />
      </div>

      {/* KPI grid */}
      <div className="mb-4 grid grid-cols-2 gap-3">
        <KPI
          label={lang === 'tn' ? 'Madi a tsenang' : 'Money in'}
          value={monthly?.totalIncome ?? 0}
          tone="green"
        />
        <KPI
          label={lang === 'tn' ? 'Madi a tswang' : 'Money out'}
          value={monthly?.totalExpense ?? 0}
          tone="red"
        />
        <KPI
          label={lang === 'tn' ? 'Phaello' : 'Net profit'}
          value={monthly?.netProfit ?? 0}
          tone={(monthly?.netProfit ?? 0) >= 0 ? 'green' : 'red'}
        />
        <KPI
          label={lang === 'tn' ? 'Madi a teng' : 'Cash balance'}
          value={cashBalance}
          tone="blue"
        />
      </div>

      {/* Cash flow */}
      {sixMonthBars && (
        <Card className="mb-4">
          <h3 className="font-display mb-3 text-lg">Last 6 months</h3>
          <div className="h-48">
            <BarChart
              data={{
                labels: sixMonthBars.labels,
                datasets: [
                  { label: 'Income', data: sixMonthBars.income, backgroundColor: '#047857' },
                  { label: 'Expense', data: sixMonthBars.expense, backgroundColor: '#B91C1C' },
                ],
              }}
              options={{
                maintainAspectRatio: false,
                responsive: true,
                scales: {
                  y: { ticks: { callback: (v) => formatMoneyCompact(Number(v), regionCode) } },
                },
              }}
            />
          </div>
        </Card>
      )}

      {/* View-specific content */}
      {view === 'accountant' && trialBalance && (
        <Card className="mb-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display flex items-center gap-2 text-lg">
              <BarChart3 size={18} /> Trial balance snapshot
            </h3>
            <Link to="/app/reports" className="text-accent text-sm">
              Full report <ChevronRight className="inline" size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-muted text-xs">Total debits</p>
              <p className="tnum font-mono text-lg">
                {formatMoney(trialBalance.totalDebit, regionCode)}
              </p>
            </div>
            <div>
              <p className="text-muted text-xs">Total credits</p>
              <p className="tnum font-mono text-lg">
                {formatMoney(trialBalance.totalCredit, regionCode)}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-muted text-xs">Difference (must be zero)</p>
              <p
                className={`tnum font-mono text-base ${Math.abs(trialBalance.totalDebit - trialBalance.totalCredit) < 0.01 ? 'text-green' : 'text-red'}`}
              >
                {formatMoney(trialBalance.totalDebit - trialBalance.totalCredit, regionCode)}
              </p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Link
              to="/app/journal"
              className="text-accent rounded-btn border-line border px-3 py-1.5 text-xs"
            >
              Open journal
            </Link>
            <Link
              to="/app/reports"
              className="text-accent rounded-btn border-line border px-3 py-1.5 text-xs"
            >
              Run reports
            </Link>
          </div>
        </Card>
      )}

      {view === 'burs' && (
        <Card className="mb-4">
          <h3 className="font-display flex items-center gap-2 text-lg">
            <BarChart3 size={18} /> {taxAuth} Tax Pack
          </h3>
          <p className="text-muted mt-1 text-sm">
            Generate VAT 002 returns and the monthly PAYE certificate from one place.
          </p>
          <Link
            to="/app/burs"
            className="text-accent mt-3 inline-flex items-center gap-1 text-sm font-medium"
          >
            Open {taxAuth} pack <ChevronRight size={14} />
          </Link>
        </Card>
      )}

      {/* Salary + major expenses (this month) */}
      {expenseBreakdown && (expenseBreakdown.salary > 0 || expenseBreakdown.major.length > 0) && (
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Card>
            <div className="mb-2 flex items-center gap-2">
              <span className="bg-blue/10 text-blue inline-flex h-9 w-9 items-center justify-center rounded-full">
                <Users size={18} />
              </span>
              <h3 className="font-display text-base">Salary expense</h3>
            </div>
            <p className="text-muted text-xs">This month, posted to ledger</p>
            <p className="font-mono text-2xl tabular-nums">
              {formatMoney(expenseBreakdown.salary, regionCode)}
            </p>
            <Link
              to="/app/payroll"
              className="text-accent mt-2 inline-flex items-center gap-1 text-xs"
            >
              Open payroll <ChevronRight size={12} />
            </Link>
          </Card>

          <Card>
            <div className="mb-2 flex items-center gap-2">
              <span className="bg-red/10 text-red inline-flex h-9 w-9 items-center justify-center rounded-full">
                <TrendingDown size={18} />
              </span>
              <h3 className="font-display text-base">Major expenses</h3>
            </div>
            {expenseBreakdown.major.length === 0 ? (
              <p className="text-muted text-sm">Nothing posted this month yet.</p>
            ) : (
              <ul className="space-y-1">
                {expenseBreakdown.major.map((m) => (
                  <li key={m.gl_code} className="flex items-baseline justify-between text-sm">
                    <span className="text-muted truncate">
                      <span className="text-muted/80 mr-1.5 font-mono text-[11px]">
                        {m.gl_code}
                      </span>
                      {m.name}
                    </span>
                    <span className="tnum font-mono">
                      {formatMoneyCompact(m.total, regionCode)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {/* Reminders */}
      {reminders.length > 0 && (
        <Card className="mb-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display flex items-center gap-2 text-lg">
              <Bell size={18} /> Reminders
            </h3>
            <Link to="/app/reminders" className="text-accent text-sm">
              See all <ChevronRight className="inline" size={14} />
            </Link>
          </div>
          <ul className="space-y-2">
            {reminders.map((r) => (
              <ReminderRow key={r.id} reminder={r} />
            ))}
          </ul>
        </Card>
      )}

      {/* Upcoming recalls — healthcare practices only */}
      {business && isHealthcareBusiness(business.industry) && (
        <div className="mb-4">
          <RecallWidget
            businessId={business.id}
            practiceName={business.trading_name ?? business.name}
            lang={lang}
          />
        </div>
      )}

      {/* Recent receipts */}
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg">{t('dash.recent_receipts')}</h3>
          <Link to="/app/receipts" className="text-accent text-sm">
            See all <ChevronRight className="inline" size={14} />
          </Link>
        </div>
        {receipts.length === 0 ? (
          <p className="text-muted text-sm">{t('receipts.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {receipts.map((r) => (
              <li
                key={r.id}
                className="border-line flex items-center justify-between border-b py-2 last:border-0"
              >
                <div>
                  <p className="text-sm font-medium">{r.merchant_name ?? 'Unknown merchant'}</p>
                  <p className="text-muted text-xs">
                    {r.receipt_date ?? r.uploaded_at?.slice(0, 10)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm">
                    {r.total_amount != null ? formatMoney(r.total_amount, regionCode) : '—'}
                  </p>
                  <Pill
                    tone={
                      r.ocr_status === 'completed'
                        ? 'green'
                        : r.ocr_status === 'failed'
                          ? 'red'
                          : 'neutral'
                    }
                    className="mt-1"
                  >
                    {r.ocr_status}
                  </Pill>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </main>
  );
}

function QuickAction({
  to,
  icon: Icon,
  title,
  subtitle,
  tone,
  badge,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  tone: 'accent' | 'green' | 'blue' | 'red';
  badge?: number;
}) {
  const ringMap: Record<string, string> = {
    accent: 'bg-[#FEF3C7] text-[#92400E]',
    green: 'bg-[#D1FAE5] text-[#065F46]',
    blue: 'bg-[#DBEAFE] text-[#1E3A8A]',
    red: 'bg-[#FEE2E2] text-[#991B1B]',
  };
  return (
    <Link
      to={to}
      className="rounded-card-lg border-line shadow-card hover:shadow-card-hover relative flex flex-col items-start gap-1 border bg-white p-3 transition-shadow"
    >
      <span
        className={`mb-1 inline-flex h-9 w-9 items-center justify-center rounded-full ${ringMap[tone]}`}
      >
        <Icon size={18} />
      </span>
      <span className="text-ink text-sm font-medium">{title}</span>
      <span className="text-muted text-xs">{subtitle}</span>
      {badge != null && badge > 0 && (
        <span className="bg-accent text-paper absolute right-2 top-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[10px] font-semibold">
          {badge}
        </span>
      )}
    </Link>
  );
}

function KPI({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'red' | 'blue' | 'accent';
}) {
  const regionCode = useRegionStore((s) => s.code);
  const colourMap: Record<string, string> = {
    green: 'text-green',
    red: 'text-red',
    blue: 'text-blue',
    accent: 'text-accent',
  };
  return (
    <Card compact>
      <p className="text-muted text-xs">{label}</p>
      <p className={`font-mono text-lg ${colourMap[tone]}`}>
        {formatMoneyCompact(value, regionCode)}
      </p>
    </Card>
  );
}

function ReminderRow({ reminder }: { reminder: Reminder }) {
  const due = new Date(reminder.due_date);
  const days = Math.ceil((due.getTime() - Date.now()) / 86_400_000);
  const tone = days < 0 ? 'red' : days <= 3 ? 'accent' : 'blue';
  const label = days < 0 ? `${-days}d overdue` : days === 0 ? 'today' : `${days}d`;
  return (
    <li className="flex items-center justify-between text-sm">
      <div>
        <p className="font-medium">{reminder.title}</p>
        <p className="text-muted text-xs">{reminder.due_date}</p>
      </div>
      <Pill tone={tone}>{label}</Pill>
    </li>
  );
}
